import { useId } from "react";
import type { CanvasEditor, CanvasEditorSnapshot } from "../controller/types";
import type { CanvasEdgePatch } from "../domain/types";
import { resolveCanvasEdgeStyle } from "../edgeStyle";
import { useCanvasMessages } from "./i18n";

export interface CanvasEdgeInspectorProps {
  editor: CanvasEditor;
  snapshot: CanvasEditorSnapshot;
}

export function CanvasEdgeInspector({
  editor,
  snapshot,
}: CanvasEdgeInspectorProps) {
  const messages = useCanvasMessages();
  const idPrefix = `canvas-${useId().replaceAll(":", "")}-edge`;
  const selectedId =
    snapshot.selection.primaryId !== undefined &&
    snapshot.selection.edgeIds.has(snapshot.selection.primaryId)
      ? snapshot.selection.primaryId
      : [...snapshot.selection.edgeIds][0];
  const edge = snapshot.canvas.edges.find(
    (candidate) => candidate.id === selectedId,
  );
  if (edge === undefined) return null;
  const style = resolveCanvasEdgeStyle(edge);
  const update = (patch: CanvasEdgePatch) =>
    editor.dispatch({ type: "update-edge", id: edge.id, patch });
  return (
    <fieldset
      aria-label={messages.edgeProperties}
      disabled={snapshot.readOnly}
      style={{
        position: "absolute",
        zIndex: 19,
        right: 12,
        bottom: 42,
        display: "grid",
        gridTemplateColumns: "auto minmax(72px, 1fr)",
        alignItems: "center",
        gap: "7px 10px",
        minWidth: 250,
        padding: 12,
        border: "1px solid #dbe3ee",
        borderRadius: 12,
        background: "rgba(255,255,255,.97)",
        boxShadow: "0 6px 24px rgba(15,23,42,.12)",
      }}
    >
      <legend style={{ padding: "0 5px", fontWeight: 650 }}>
        {messages.edgeProperties}
      </legend>
      <label htmlFor={`${idPrefix}-label`}>{messages.edgeLabel}</label>
      <input
        id={`${idPrefix}-label`}
        value={edge.label ?? ""}
        onChange={(event) => update({ label: event.currentTarget.value })}
      />
      <label htmlFor={`${idPrefix}-color`}>{messages.edgeColor}</label>
      <input
        id={`${idPrefix}-color`}
        type="color"
        value={style.color}
        onChange={(event) =>
          update({ props: { color: event.currentTarget.value } })
        }
      />
      <label htmlFor={`${idPrefix}-width`}>{messages.edgeWidth}</label>
      <input
        id={`${idPrefix}-width`}
        type="number"
        min={0.5}
        max={24}
        step={0.5}
        value={style.width}
        onChange={(event) => {
          const width = event.currentTarget.valueAsNumber;
          if (!Number.isFinite(width) || width <= 0) return;
          update({ props: { width: Math.min(24, Math.max(0.5, width)) } });
        }}
      />
      <label htmlFor={`${idPrefix}-dash`}>连线线型</label>
      <select id={`${idPrefix}-dash`} value={style.dash} onChange={(event) => update({ props: { dash: event.currentTarget.value } })}><option value="solid">实线</option><option value="dashed">虚线</option></select>
      <label htmlFor={`${idPrefix}-start`}>{messages.arrowStart}</label>
      <input
        id={`${idPrefix}-start`}
        type="checkbox"
        checked={style.arrowStart}
        onChange={(event) =>
          update({ props: { arrowStart: event.currentTarget.checked } })
        }
      />
      <label htmlFor={`${idPrefix}-end`}>{messages.arrowEnd}</label>
      <input
        id={`${idPrefix}-end`}
        type="checkbox"
        checked={style.arrowEnd}
        onChange={(event) =>
          update({ props: { arrowEnd: event.currentTarget.checked } })
        }
      />
    </fieldset>
  );
}
